import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Button, Box, MenuItem, Typography, FormControl, InputLabel, Select } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { DatePicker, MuiPickersUtilsProvider } from '@/compat/pickers';
import DateFnsUtils from '@date-io/date-fns';
import EnhancedTableHead from '../../../components/table/EnhancedTableHead';
import { sortOrderName } from '../../constants/sortOrder';
import SeekAdviceDialog from '../dialogs/SeekAdviceDialog';
import { useAuth } from '../../../contexts/authContext';
import HealthAndSafetyIcon from '../icons/HealthAndSafetyIcon';
import { useSelector } from 'react-redux';
import adviceService from '../../services/adviceService';
import BlockIcon from '@mui/icons-material/NoSimOutlined';
import { useHistory } from "@/compat/router";


const useStyles = makeStyles((theme) => ({
    searchField: {
        marginBottom: theme.spacing(2),
    },
    filtersContainer: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        alignItems: 'center',
    },
    filterItem: {
        minWidth: 200,
        flex: 1,
    },
    tableContainer: {
        marginTop: theme.spacing(2),
    },
    editButton: {
        minWidth: 'auto',
        padding: theme.spacing(0.5),
    },
    seekAdviceButton: {
        backgroundColor: '#5c45c7',
        color: 'white',
        '&:hover': {
            backgroundColor: '#4a3aa0',
        },
    },
    filterBox: {
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'flex-start',
    },
    checkboxContainer: {
        marginTop: theme.spacing(2),
    },
    filterActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2)
    },
    emptyStateContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(4),
        textAlign: 'center',
    },
    emptyStateIcon: {
        width: 80,
        height: 80,
        borderRadius: '50%',
        backgroundColor: '#EBE9FC',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: theme.spacing(2),
    },
    emptyStateTitle: {
        fontSize: '1.1rem',
        fontWeight: 500,
        marginBottom: theme.spacing(1),
    },
    emptyStateSubtitle: {
        color: theme.palette.text.secondary,
    },
    tableRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
}));

const headCells = [
    { id: 'dateRaised', label: 'Date Raised', isSortable: true },
    { id: 'site', label: 'Site', isSortable: true },
    { id: 'user', label: 'Contact', isSortable: true },
    { id: 'consultant', label: 'Consultant', isSortable: true },
    { id: 'title', label: 'Title', isSortable: true },
    { id: 'type', label: 'Type', isSortable: true },
    { id: 'responses', label: 'Responses', isSortable: true },
    { id: 'actions', label: '', isSortable: false },
];

export default function AdviceTableCard() {
    const classes = useStyles();
    const {
        isSafetyNestSeekAdviceAllowedForSites,
        isSafetyNestViewAdviceAllowedForSites,
        isSafetyNestAllAdviceAllowedForSites,
        user
    } = useAuth();
    const { selectedSite, appSiteIds, activeAppId } = useSelector((state) => state.account);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [sortBy, setSortBy] = useState('dateRaised');
    const [orderBy, setOrderBy] = useState(sortOrderName.DESC);

    const [selectedUser, setSelectedUser] = useState('');
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [selectedAdviceType, setSelectedAdviceType] = useState('');
    const [availableUsers, setAvailableUsers] = useState([]);
    const [adviceTypes, setAdviceTypes] = useState([]);    

    const [dialogOpen, setDialogOpen] = useState(false);
    const [message, setMessage] = useState('');    

    const siteExternalIds = selectedSite
        ? [selectedSite.externalId]
        : appSiteIds[activeAppId];

    const canViewAdvice = isSafetyNestViewAdviceAllowedForSites;
    const canSeekAdvice = isSafetyNestSeekAdviceAllowedForSites;
    const canViewAllAdvice = isSafetyNestAllAdviceAllowedForSites;

    const history = useHistory();

    useEffect(() => {
        setPage(0);
    }, [selectedSite, activeAppId]);

    const formattedFromDate = fromDate ? fromDate.toISOString().split('T')[0] : null;
    const formattedToDate = toDate ? toDate.toISOString().split('T')[0] : null;

    const { data, isLoading, error, refetch } = adviceService.useAdviceData(
        siteExternalIds,
        page,
        rowsPerPage,
        sortBy,
        orderBy,
        canViewAllAdvice,
        user?.details?.userId,
        formattedFromDate,
        formattedToDate,
        selectedUser,
        selectedAdviceType
    );

    adviceService.useAdviceFilterOptions(
        siteExternalIds,
        (data) => {
            if (data && data.advisoryTypes) {
                setAdviceTypes(data.advisoryTypes);
            }
            if (data && data.contacts) {
                setAvailableUsers(data.contacts);
            }
        }
    );   

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (event, property) => {
        const isAsc = sortBy === property && orderBy === sortOrderName.ASC;
        setOrderBy(isAsc ? sortOrderName.DESC : sortOrderName.ASC);
        setSortBy(property);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setMessage('');
    };

    const handleSubmit = async () => {
        try {
            await adviceService.seekAdvice({
                message,
                attachments: [],
            });
            handleDialogClose();
        } catch (error) {
            console.error('Failed to submit advice request:', error);
        }
    };

    const handleAdviceTypeSelect = (e) => {
        setSelectedAdviceType(e.target.value);
        setTimeout(() => refetch(), 0);
    };

    const hasActiveFilters = fromDate !== null || toDate !== null || selectedUser !== '' || selectedAdviceType !== '';

    const handleFromDateChange = (date) => {
        setFromDate(date);
        setTimeout(() => refetch(), 0);
    };

    const handleToDateChange = (date) => {
        setToDate(date);
        setTimeout(() => refetch(), 0);
    };

    const handleUserChange = (e) => {
        setSelectedUser(e.target.value);
        setTimeout(() => refetch(), 0);
    };

    const handleClearFilters = () => {
        setFromDate(null);
        setToDate(null);
        setSelectedUser('');
        setSelectedAdviceType('');

        setTimeout(() => {
            refetch();
        }, 0);
    };

    const handleRowClick = (id) => {
        history.push(`/safetynest/advice/detail/${id}`);
    };

    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">H&S Advice Record</Typography>
                {canSeekAdvice && (
                    <Button
                        variant="contained"
                        className={classes.seekAdviceButton}
                        startIcon={<HealthAndSafetyIcon />}
                        onClick={() => setDialogOpen(true)}
                    >
                        Seek Advice
                    </Button>
                )}
            </Box>

            {canViewAdvice && (
                <Box className={classes.filtersContainer}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <DatePicker
                            inputVariant="outlined"
                            variant="inline"
                            format="MM/dd/yyyy"
                            id="from-date-picker"
                            label="From Date"
                            value={fromDate}
                            onChange={handleFromDateChange}
                            className={classes.filterItem}
                        />

                        <DatePicker
                            inputVariant="outlined"
                            variant="inline"
                            format="MM/dd/yyyy"
                            id="to-date-picker"
                            label="To Date"
                            value={toDate}
                            onChange={handleToDateChange}
                            className={classes.filterItem}
                        />
                    </MuiPickersUtilsProvider>

                    <FormControl variant="outlined" size="small" className={classes.filterItem}>
                        <InputLabel id="user-select-label">Select Contact</InputLabel>
                        <Select
                            labelId="user-select-label"
                            value={selectedUser}
                            onChange={handleUserChange}
                            label="Select Contact"
                        >
                            <MenuItem value="">All</MenuItem>
                            {availableUsers.map(user => (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" className={classes.filterItem}>
                        <InputLabel id="advice-type-label">Type</InputLabel>
                        <Select
                            labelId="advice-type-label"
                            value={selectedAdviceType}
                            onChange={handleAdviceTypeSelect}
                            label="Type"
                        >
                            <MenuItem value="">All</MenuItem>
                            {adviceTypes.map(type => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {hasActiveFilters && (
                        <Box className={classes.filterActions}>
                            <Button
                                variant="outlined"
                                onClick={handleClearFilters}
                            >
                                Clear Filters
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {/* Table Section */}
            <TableContainer className={classes.tableContainer}>
                <Table>
                    <EnhancedTableHead
                        headCells={headCells}
                        sortColumn={sortBy}
                        sortDirection={orderBy}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                        {canViewAdvice && data?.results && data.results.length > 0 ? (
                            data.results.map((row) => (
                                <TableRow
                                    key={row.id}
                                    onClick={() => handleRowClick(row.id)}
                                    className={classes.tableRow}
                                >
                                    <TableCell>{new Date(row.startTime).toLocaleDateString()}</TableCell>
                                    <TableCell>{row.accountName}</TableCell>
                                    <TableCell>{row.contactName || row.ContactName || '-'}</TableCell>
                                    <TableCell>{row.consultant || '-'}</TableCell>
                                    <TableCell>{row.description}</TableCell>
                                    <TableCell>{row.activityType}</TableCell>
                                    <TableCell align="center">{row.childCount || 0}</TableCell>

                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={8}>
                                    <Box className={classes.emptyStateContainer}>
                                        <Box className={classes.emptyStateIcon}>
                                            <BlockIcon style={{ color: '#5C45C7', fontSize: 28 }} />
                                        </Box>
                                        <Typography className={classes.emptyStateTitle}>
                                            No Advice Records...
                                        </Typography>
                                        <Typography className={classes.emptyStateSubtitle}>
                                            There are currently no advice records.
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={canViewAdvice ? (data?.totalCount || 0) : 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                />
            </TableContainer>

            <SeekAdviceDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                message={message}
                onMessageChange={(e) => setMessage(e.target.value)}
                onSubmit={handleSubmit}
                titleText="Seek Advice"
            />
        </>
    );
}